import type {SearchResult} from '@/services/business-search/model'
import {http} from '@/services/http'
import {BusinessEntryModeEventListener} from './business-entry-mode-event-listener'
import * as formElements from './form-elements'

// Lazy load autocomplete library
let autoCompleteLibraryPromise: Promise<AutoCompleteConstructor> | null = null

async function loadAutoCompleteLibrary(): Promise<AutoCompleteConstructor> {
	const win = globalThis as typeof globalThis & Window

	// If already loaded globally, return it immediately
	if (win.autoComplete) {
		return win.autoComplete
	}

	// If currently loading, return the existing promise
	if (autoCompleteLibraryPromise) {
		return autoCompleteLibraryPromise
	}

	// Check if script already exists in DOM
	const existingScript = document.querySelector(
		'script[src*="autoComplete.min.js"]'
	)
	if (existingScript) {
		// Script exists but library not loaded yet - wait for it
		autoCompleteLibraryPromise = new Promise((resolve, reject) => {
			const checkInterval = setInterval(() => {
				if (win.autoComplete) {
					clearInterval(checkInterval)
					resolve(win.autoComplete)
				}
			}, 100)

			// Timeout after 10 seconds
			setTimeout(() => {
				clearInterval(checkInterval)
				autoCompleteLibraryPromise = null
				reject(new Error('AutoComplete library load timeout'))
			}, 10000)
		})
		return autoCompleteLibraryPromise
	}

	// Load the library for the first time
	autoCompleteLibraryPromise = new Promise((resolve, reject) => {
		const script = document.createElement('script')
		script.src =
			'https://cdn.jsdelivr.net/npm/@tarekraafat/autocomplete.js@10.2.9/dist/autoComplete.min.js'
		script.async = true

		const timeoutId = globalThis.setTimeout(() => {
			// Neither onload nor onerror fired in time — remove the hanging script
			// so the next setup attempt can append a fresh one.
			script.remove()
			autoCompleteLibraryPromise = null
			reject(new Error('AutoComplete library load timeout'))
		}, 10_000)

		script.onload = () => {
			clearTimeout(timeoutId)
			// The library exposes itself as a global
			if (win.autoComplete) {
				resolve(win.autoComplete)
			} else {
				// Script loaded but library is missing — treat as failure so the next
				// setup attempt appends a fresh script instead of waiting on this one.
				script.remove()
				autoCompleteLibraryPromise = null
				reject(new Error('AutoComplete library failed to load'))
			}
		}
		script.onerror = () => {
			clearTimeout(timeoutId)
			// Remove the failed element so the next setup attempt is not blocked
			// by the existingScript guard.
			script.remove()
			autoCompleteLibraryPromise = null
			reject(new Error('Failed to load AutoComplete script'))
		}
		document.head.appendChild(script)
	})

	return autoCompleteLibraryPromise
}

export interface SearchParams {
	search?: string
	countryCode?: string
	limit?: number
	businessTypes?: string
}

interface SearchResponse {
	success: boolean
	data: {
		'@odata.context': string
		resultCount: number
		value: SearchResult[]
	}
}

let CountryCode = ''

function setCountryCode(locale: string): void {
	CountryCode =
		locale === '' || locale === 'testing'
			? 'US'
			: locale.toLocaleUpperCase()
}

function hideResultLabel(): void {
	const label = formElements.BusinessResultLabel()
	if (label) {
		label.innerHTML = ''
		label.style.display = 'none'
	}
}

function setSelectionLabel(selection: {
	Name: string
	location: {
		address: string
		city: string
		state: string
		postalCode: string
	}
}): void {
	const label = formElements.BusinessResultLabel()
	if (label) {
		label.innerHTML = `<span style="font-weight:bold;">${selection.Name}</span><br>${selection.location.address}, ${selection.location.city}, ${selection.location.state} ${selection.location.postalCode}`
		label.style.display = 'inline'
	}
}

function setNoResultsLabel(
	query: string,
	noResultText: string | undefined
): void {
	const label = formElements.BusinessResultLabel()
	if (label && noResultText) {
		label.innerHTML = `<span style="font-weight:bold;">'${noResultText} ${query}'</span>`
		label.style.display = 'inline-block'
	}
}

function setServiceErrorLabel(): void {
	const label = formElements.BusinessResultLabel()
	if (label) {
		label.innerHTML = `<span style="font-weight:bold;">The business search service is currently unavailable. Please try again later or enter your business manually.</span>`
		label.style.display = 'inline-block'
	}
}

function setBusinessSearchError(message: string): void {
	const label = formElements.BusinessSearchErrorLabel()
	if (label) {
		formElements.setErrorLabel(label, message)
	}
}

function clearBusinessSearchError(): void {
	const label = formElements.BusinessSearchErrorLabel()
	if (label) {
		formElements.setErrorLabel(label, '')
	}
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- implicit is better here
async function getData(query: string, noResultText: string | undefined) {
	// Define a variable to check if CountryCode is 'US' or 'AU'
	const isUSOrAU = CountryCode === 'US' || CountryCode === 'AU'

	if (!isUSOrAU && query.length < 3) {
		return []
	}
	const requestUrl = new URL(
		'/api/business/search',
		globalThis.location.origin
	)
	requestUrl.searchParams.set('search', query)
	requestUrl.searchParams.set('countryCode', CountryCode)

	try {
		const {data} = await http.get<SearchResponse>(
			`${requestUrl.pathname}${requestUrl.search}`
		)
		const renderCount = data.data.resultCount
		if (CountryCode === 'US' || CountryCode === 'AU') return data.data.value
		if (renderCount === 0) {
			const zeroResults = noResultText ? `${noResultText} "${query}"` : ''
			return [{zeroResults}]
		}
		return data.data.value
	} catch (error) {
		return [{error: JSON.stringify(error)}]
	}
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- implicit is better here
function createAutoCompleteConfig(noResultText: string) {
	return {
		selector: '.autocomplete-sep input[id^=gigya-textbox]',
		placeHolder: '', // This value will override by CDC. TODO: Get this value from Kontent.ai
		debounce: 500,
		data: {
			src: getData,
			cache: false,
		},
		searchEngine: (
			_query: string,
			record: {
				error?: string
				errorMessage?: string
				nullValueMessage?: string
				zeroResults?: string
				Name: string
				location: {
					address: string
					city: string
					state: string
					postalCode: string
				}
			}
		) => {
			if (record.error) return 'error'
			else if (record.errorMessage) return record.errorMessage
			else if (record.nullValueMessage) return record.nullValueMessage
			else if (record.zeroResults || record.zeroResults === '')
				return record.zeroResults
			return `<b>${record.Name}</b> <br>${record.location.address}, ${record.location.city}, ${record.location.state} ${record.location.postalCode}<br><br>`
		},
		resultsList: {
			maxResults: 50,
			element: (
				_list: unknown,
				data: {
					results: {
						value: {
							error?: {
								message: string
							}
						}
					}[]
					query: string
				}
			) => {
				if (!data.results.length) {
					setNoResultsLabel(data.query, noResultText)
				}

				if (
					data.results[0]?.value.error &&
					data.results[0].value.error.message === 'Failed to fetch'
				) {
					setServiceErrorLabel()
				}
			},
			noResults: true,
		},
		resultItem: {
			highlight: true,
		},
	}
}
interface BusinessData {
	phone: string | number | undefined
	crmId: string
	type: string
	name: string
	address1: string
	address2?: string
	city: string
	state: string
	zip: string
	country: string
}

class BusinessSearch {
	private searchDisabled = false

	private manualBusinessEntryToggled = false

	private businessData: BusinessData | null = null

	private noResultText: string | undefined = undefined
	constructor(noResultText?: string) {
		this.noResultText = noResultText
		this.searchDisabled = !(formElements.BusinessSearchField() !== null)
	}

	async init(locale: string): Promise<void> {
		if (this.searchDisabled) return

		const toggle = formElements.BusinessToggle()
		toggle?.addEventListener('change', (event) => {
			this.manualBusinessEntryToggled = !this.manualBusinessEntryToggled
			this.businessData = null
			clearBusinessSearchError()
			BusinessEntryModeEventListener(event)
		})

		hideResultLabel()
		await this.setupAutoComplete(locale)
	}

	validate(): boolean {
		if (this.searchDisabled) return true

		const noBusinessSelected = this.businessData === null
		const searchModeEnabled = !this.manualBusinessEntryToggled
		if (noBusinessSelected && searchModeEnabled) {
			setBusinessSearchError('Please select or manually enter a business')
			return false
		}

		clearBusinessSearchError()
		return true
	}

	getBusinessData(): BusinessData | null {
		if (this.manualBusinessEntryToggled) {
			return {
				crmId: '',
				type: formElements.getTypeFieldValue() ?? '',
				name: formElements.getNameFieldValue() ?? '',
				address1: formElements.getAddress1FieldValue() ?? '',
				address2: formElements.getAddress2FieldValue(),
				city: formElements.getCityFieldValue() ?? '',
				state: formElements.getStateFieldValue() ?? '',
				zip: formElements.getZipFieldValue() ?? '',
				phone: formElements.getPhoneFieldValue() ?? '',
				country: CountryCode,
			}
		}

		return this.businessData
	}

	async setupAutoComplete(locale: string): Promise<void> {
		if (this.searchDisabled) return
		setCountryCode(locale)

		// Lazy load the autocomplete library
		const AutoComplete = await loadAutoCompleteLibrary()

		const autoComplete = new AutoComplete({
			...createAutoCompleteConfig(this.noResultText || ''),
			data: {
				...createAutoCompleteConfig(this.noResultText || '').data,
				src: (query: string) => getData(query, this.noResultText),
			},
		})
		autoComplete.input.addEventListener(
			'selection',
			// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Library uses custom events without type declarations
			(event: any) => {
				// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- Library has no type declarations
				const feedback = event.detail
				autoComplete.input.blur()
				// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- Library has no type declarations
				const selection = feedback.selection.value
				this.businessData = {
					// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- Library has no type declarations
					crmId: selection.CRMId,
					// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- Library has no type declarations
					type: selection.AccountClassification,
					// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- Library has no type declarations
					name: selection.Name,
					phone: formElements.getPhoneFieldValue() ?? '',
					// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- Library has no type declarations
					address1: selection.location.address,
					// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- Library has no type declarations
					address2: selection.location.address2,
					// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- Library has no type declarations
					city: selection.location.city,
					// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- Library has no type declarations
					state: selection.location.state,
					// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- Library has no type declarations
					zip: selection.location.postalCode,
					// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- Library has no type declarations
					country: selection.location.countryCode,
				}

				// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment -- Library has no type declarations
				autoComplete.input.value = feedback.query
				// eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- Library has no type declarations
				setSelectionLabel(selection)
			}
		)

		autoComplete.input.addEventListener('input', () => {
			hideResultLabel()
			clearBusinessSearchError()
			this.businessData = null
		})

		autoComplete.list.style.overflowY = 'scroll'
		autoComplete.list.style.maxHeight = '200px'
		autoComplete.list.style.border = '1px solid rgba(33, 33, 33, 0.07)'
		autoComplete.list.classList.add('gigya-style-modern')
	}
}

// eslint-disable-next-line @typescript-eslint/no-extraneous-class -- Original design
class BusinessSearchFactory {
	static instance: BusinessSearch

	constructor() {
		throw new Error('Use BusinessSearchFactory.getInstance()')
	}

	static getInstance(noResultText?: string): BusinessSearch {
		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Original design
		if (!BusinessSearchFactory.instance) {
			BusinessSearchFactory.instance = new BusinessSearch(noResultText)
		}

		return BusinessSearchFactory.instance
	}
}

async function setupBusinessSearch(
	locale: string,
	noResultText?: string
): Promise<void> {
	await BusinessSearchFactory.getInstance(noResultText).init(locale)
}

function validateBusiness(): boolean {
	return BusinessSearchFactory.getInstance().validate()
}

function getBusinessData(): BusinessData | null {
	return BusinessSearchFactory.getInstance().getBusinessData()
}

export {setupBusinessSearch, validateBusiness, getBusinessData}
